<template>
  <div
    v-loading="isLoading"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <div class="flex-1 flex justify-center items-center">
      <KeyboardPanel
        :rgb="keyboardRgb"
        :rgb-group="keyboardRgbGroup"
        :is-colorful="keyboardIsColorful"
        :gradient-direction="keyboardGradientDirection"
        :current-effect-info="keyboardEffectInfo"
        :key-map-rgb="keyboardKeyMapRGB"
        :selected-keys="selectedKeys"
        :can-edit-selection="canEditKeySelection"
        :indicator-styles="indicatorWidgetStyles"
        :fill-keys="activeLightType === LIGHTING_TARGET.AudioRhythm"
        @selection-change="handleSelectedKeysChange"
      />
    </div>

    <div class="flex flex-col w-full gap-4.5 h-82 shrink-0 mb-2">
      <div class="h-10 shrink-0 flex gap-4 justify-center">
        <div class="p-0 flex gap-4 justify-center">
          <Button
            v-for="tab in lightTabs"
            :key="tab.i18nKey"
            color="glass"
            :active="activeLightType === tab.type"
            class="h-10"
            @click="setLightType(tab.type)"
          >
            {{ $t(tab.i18nKey) }}
          </Button>
        </div>
      </div>
      <div class="h-67.5 w-full flex justify-center gap-2">
        <ColorPicker
          v-if="activeLightType !== LIGHTING_TARGET.AudioRhythm"
          v-model="rgb"
          v-model:rgb-group="rgbGroup"
          v-model:is-colorful="isColorful"
          :gradient-direction="gradientDirection"
          :current-effect-info="currentEffectInfo"
          @change="handleColorChange"
        />
        <effectsList
          v-model="currentMode"
          :effects="effects"
          :enabled-side-lighting="enabledSideLighting"
          :backlight-switch="hasBacklightSwitch"
          :enabled-backlight="enabledBacklight"
          :type="activeLightType"
          :audio-rhythm-status="audioRhythmCaptureStatus"
          :audio-rhythm-using-mic="audioRhythmCaptureUsingMic"
          @change="setMode"
          @custom-mode="setCustomMode"
          @switch-side-lighting="setSideEnabled"
          @switch-backlight="setBacklightEnabled"
          @toggle-audio-rhythm="toggleAudioRhythmCapture"
        />
        <ColorList
          v-if="activeLightType === LIGHTING_TARGET.AudioRhythm"
          v-model="audioRhythmColorId"
        />
        <AudioRhythmSettings
          v-if="activeLightType === LIGHTING_TARGET.AudioRhythm"
          v-model="audioRhythmSettings"
        />

        <div v-if="rightOperations" class="w-101 flex flex-col gap-2">
          <brightSlider
            v-if="canEditCurrent(LIGHTING_EDITABLE_FIELD.Brightness)"
            v-model="brightness"
            :max="brightnessRange.max"
            :step="brightnessRange.step"
            @change="setBrightness()"
          />

          <speedSlider
            v-if="canEditCurrent(LIGHTING_EDITABLE_FIELD.Speed)"
            v-model="speed"
            :max="speedRange.max"
            :step="speedRange.step"
            @change="setSpeed()"
          />

          <grandientSwitch
            v-if="canEditCurrent(LIGHTING_EDITABLE_FIELD.Gradient)"
            v-model="rgbGroup"
            :gradient-direction="gradientDirection"
            :current-effect-info="currentEffectInfo"
            @change="setGradient"
          />

          <directionSwitch
            v-if="canEditCurrent(LIGHTING_EDITABLE_FIELD.Direction)"
            v-model="direction"
            :current-effect-info="currentEffectInfo"
            @change="setDirection"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { Button } from '@rapoo/element'
  import {
    hasLightingEditableField,
    LIGHTING_EDITABLE_FIELD,
    LIGHTING_TARGET,
    type KeyboardLightingTarget,
    type lightingColor,
    type LightingEditableField,
  } from '@rapoo/device-sdk'
  import KeyboardPanel from './components/keyboardPanel/index.vue'
  import ColorPicker from './components/colorPicker/index.vue'
  import ColorList from './components/ColorList.vue'
  import AudioRhythmSettings from './components/AudioRhythmSettings.vue'
  import effectsList from './components/effectsList/index.vue'
  import brightSlider from './components/brightSlider/index.vue'
  import speedSlider from './components/speedSlider/index.vue'
  import grandientSwitch from './components/grandientSwitch/index.vue'
  import directionSwitch from './components/directionSwitch/index.vue'

  import { useLighting } from './composables/useLighting'
  import { useKeySelection } from './composables/useKeySelection'

  defineOptions({ name: 'KeyboardLightingPage' })

  const {
    isLoading,
    activeLightType,
    lightingCapability,
    hasSideLighting,
    hasBacklightSwitch,
    hasIndicator,
    hasAudioRhythm,
    enabledSideLighting,
    enabledBacklight,
    keyboardRgb,
    keyboardRgbGroup,
    keyboardIsColorful,
    keyboardGradientDirection,
    keyboardKeyMapRGB,
    keyboardEffectInfo,
    rgb,
    rgbGroup,
    isColorful,
    effects,
    currentMode,
    brightness,
    speed,
    speedRange,
    brightnessRange,
    gradientDirection,
    direction,
    currentEffectInfo,
    audioRhythmColorId,
    audioRhythmSettings,
    audioRhythmCaptureStatus,
    audioRhythmCaptureUsingMic,
    toggleAudioRhythmCapture,
    indicatorWidgetStyles,
    setLightType,
    setMode,
    setColor,
    setCustomMode,
    setBrightness,
    setSpeed,
    setGradient,
    setDirection,
    setSideEnabled,
    setBacklightEnabled,
  } = useLighting()

  const lightTabs = computed(() => {
    const tabI18nKeys = lightingCapability.value?.config.tabI18nKeys
    const tab = (type: KeyboardLightingTarget, fallback: string) => ({
      i18nKey: tabI18nKeys?.[type] ?? fallback,
      type,
    })

    const tabs = [tab(LIGHTING_TARGET.Keyboard, 'lighting.tabs.keyLights')]

    if (hasSideLighting.value) {
      tabs.push(tab(LIGHTING_TARGET.Side, 'lighting.tabs.sideLights'))
    }

    if (hasAudioRhythm.value) {
      tabs.push(tab(LIGHTING_TARGET.AudioRhythm, 'lighting.tabs.audioRhythm'))
    }

    if (hasIndicator.value) {
      tabs.push(tab(LIGHTING_TARGET.Indicator, 'lighting.tabs.indicatorLight'))
    }

    return tabs.length <= 1 ? [] : tabs
  })

  /** 仅按键灯 Tab 可编辑逐键选区 */
  const selectionEffectInfo = computed(() =>
    activeLightType.value === LIGHTING_TARGET.Keyboard ? keyboardEffectInfo.value : undefined,
  )

  const { selectedKeys, canEditKeySelection, handleSelectedKeysChange } =
    useKeySelection(selectionEffectInfo)

  const handleColorChange = (color: lightingColor) => {
    return setColor(color, selectedKeys.value)
  }

  const canEditCurrent = (field: LightingEditableField) =>
    hasLightingEditableField(currentEffectInfo.value ?? undefined, field)

  const rightOperations = computed(() => {
    return [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Gradient,
      LIGHTING_EDITABLE_FIELD.Direction,
    ].some((field) => canEditCurrent(field))
  })
</script>
